import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import { getMyGroups, GroupResponse } from '../logic/groupCreation/groupApi';
import { getGroupOverview, GroupOverviewResponse } from '../logic/groups/groupOverviewApi';

interface SowbAttribute {
  id: string;
  name: string;
  score: number;
  trend: string;
  status: 'Good' | 'Fair' | 'Poor';
  description: string;
  icon: string;
}

type DataScope = 'consultant' | 'enterprise';

interface SowbBreakdownPageProps {
  scope?: DataScope;
}

const ConsultantSowbBreakdownPage: React.FC<SowbBreakdownPageProps> = ({ scope = 'consultant' }) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const GROUP_STORAGE_KEY = 'consultantOverviewSelectedGroupId';

  const [groups, setGroups] = useState<GroupResponse[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(scope === 'consultant');
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() =>
    localStorage.getItem(GROUP_STORAGE_KEY)
  );

  const [overview, setOverview] = useState<GroupOverviewResponse['data'] | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  useEffect(() => {
    if (scope !== 'consultant') return;

    const fetchGroups = async () => {
      try {
        setGroupsLoading(true);
        setGroupsError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await getMyGroups(token, 1, 1000);
        const loadedGroups = response.data.groups || [];

        setGroups(loadedGroups);

        const stored = localStorage.getItem(GROUP_STORAGE_KEY);
        const storedStillValid = stored && loadedGroups.some((g) => g.id === stored);

        if (storedStillValid) {
          setSelectedGroupId(stored);
        } else if (loadedGroups.length > 0) {
          setSelectedGroupId(loadedGroups[0].id);
          localStorage.setItem(GROUP_STORAGE_KEY, loadedGroups[0].id);
        } else {
          setSelectedGroupId(null);
          localStorage.removeItem(GROUP_STORAGE_KEY);
        }
      } catch (e: any) {

        setGroups([]);
        setGroupsError(e?.message || 'Failed to load groups.');
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchGroups();
  }, [navigate, scope]);

  useEffect(() => {
    if (scope !== 'consultant') return;
    if (!selectedGroupId) {
      setOverview(null);
      return;
    }

    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        setOverviewError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await getGroupOverview(token, selectedGroupId, undefined, 'sowb');
        setOverview(response.data);
      } catch (e: any) {

        setOverview(null);
        setOverviewError(e?.message || 'Failed to load group overview.');
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, [navigate, scope, selectedGroupId]);

  // Filter states
  const [selectedTimeframe, setSelectedTimeframe] = useState('current');
  const [selectedGender, setSelectedGender] = useState('All');
  const [selectedAgeRange, setSelectedAgeRange] = useState('All Ages');
  const [selectedDepartment, setSelectedDepartment] = useState('All Departments');
  const [selectedCohort, setSelectedCohort] = useState(scope === 'enterprise' ? 'All Departments' : 'BS Political Science');

  const axis = useMemo(
    () => [
      { key: 'spiritual', label: 'Spiritual', description: 'Meaning, purpose, and inner peace', icon: 'self_improvement' },
      { key: 'nutritional', label: 'Nutritional', description: 'Dietary habits and intake', icon: 'restaurant' },
      { key: 'physical', label: 'Physical', description: 'Activity levels and health', icon: 'fitness_center' },
      { key: 'emotioPsychoRational', label: 'Emotio-Psycho-Rational', description: 'Mental and emotional balance', icon: 'psychology' },
      { key: 'legendarial', label: 'Legendarial', description: 'Legacy and personal story', icon: 'history_edu' },
      { key: 'religioTraditioCultural', label: 'Religio-Traditio-Cultural', description: 'Cultural and traditional values', icon: 'account_balance' },
      { key: 'financial', label: 'Financial', description: 'Economic stability', icon: 'payments' },
      { key: 'environmental', label: 'Environmental', description: 'Living environment and surroundings', icon: 'nature' },
      { key: 'socioRelational', label: 'Socio-Relational', description: 'Relationships and community', icon: 'diversity_3' },
      { key: 'occupational', label: 'Occupational', description: 'Career and daily work', icon: 'work' }
    ],
    []
  );

  const display10 = (v0to4: number) => (v0to4 * 2.5);

  const sowbAttributes: SowbAttribute[] = useMemo(() => {
    if (scope !== 'consultant' || !overview) {
      // Return mock data for enterprise or when no overview data
      return axis.map((a) => ({
        id: a.key,
        name: a.label,
        score: 7.5, // Mock score
        trend: 'flat' as const, // Mock trend
        status: 'Fair' as const,
        description: a.description,
        icon: a.icon
      }));
    }

    return axis.map((a) => {
      const v0to4 = overview.averages?.attributes0to4?.[a.key] ?? 0;
      const score10 = display10(v0to4);
      const trend = overview.trends?.[a.key] ?? 'flat';

      // Derive status from score10 using same thresholds as plan
      let status: 'Good' | 'Fair' | 'Poor';
      if (score10 >= 6.25) {
        status = 'Good';
      } else if (score10 >= 3.75) {
        status = 'Fair';
      } else {
        status = 'Poor';
      }

      return {
        id: a.key,
        name: a.label,
        score: score10 > 0 ? Number(score10.toFixed(1)) : 0,
        trend: trend, // Use the trend string directly
        status,
        description: a.description,
        icon: a.icon
      };
    });
  }, [axis, overview, scope]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Good':
        return 'emerald';
      case 'Fair':
        return 'amber';
      case 'Poor':
        return 'rose';
      default:
        return 'gray';
    }
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return 'trending_up';
    if (trend === 'down') return 'trending_down';
    return 'trending_flat';
  };

  const getTrendColor = (trend: string) => {
    if (trend === 'up') return 'emerald';
    if (trend === 'down') return 'rose';
    return 'gray';
  };

  return (
    <div className={`consultant-sowb-breakdown ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-sowb-breakdown__container">
        {/* Header */}
        <div className="consultant-sowb-breakdown__header">
          <Link
            to="/panel/consultant"
            className="consultant-sowb-breakdown__back-link"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Back to Dashboard
          </Link>
          <div className="consultant-sowb-breakdown__header-content">
            <h1 className="consultant-sowb-breakdown__title">
              SOWB10 Scores - Detailed Breakdown
            </h1>
            <p className="consultant-sowb-breakdown__subtitle">
              {scope === 'consultant'
                ? `Detailed analysis of well-being attributes for ${selectedGroup?.groupName || 'selected group'}. Drill down into specific rows for granular data.`
                : 'Detailed analysis of well-being attributes for the selected cohort. Drill down into specific rows for granular data.'
              }
            </p>
          </div>
          <button className="consultant-sowb-breakdown__export-btn">
            <span className="material-symbols-outlined">download</span>
            <span>Export Report</span>
          </button>
        </div>

        {scope === 'consultant' && !groupsLoading && groups.length === 0 && (
          <div className="consultant-sowb-breakdown__empty-state">
            <div className="consultant-sowb-breakdown__card">
              <div className="consultant-sowb-breakdown__empty-state-content">
                <h3 className="consultant-sowb-breakdown__card-title">No groups created yet</h3>
                <p className="consultant-sowb-breakdown__card-subtitle">
                  Create a group to view detailed well-being attribute breakdowns.
                </p>
                <button
                  className="btn btn-primary consultant-sowb-breakdown__empty-state-cta"
                  onClick={() => navigate('/panel/consultant/group-creation')}
                >
                  Go to Group Creation
                </button>
              </div>
              {groupsError && (
                <p className="consultant-sowb-breakdown__empty-state-error">
                  {groupsError}
                </p>
              )}
            </div>
          </div>
        )}

        {(scope === 'consultant' && (overviewLoading || overviewError)) && groups.length > 0 && (
          <div className="consultant-sowb-breakdown__status-row">
            {overviewLoading && <span className="consultant-sowb-breakdown__status-text">Loading overview...</span>}
            {overviewError && <span className="consultant-sowb-breakdown__status-text consultant-sowb-breakdown__status-text--error">{overviewError}</span>}
          </div>
        )}

        {(scope === 'enterprise' || (scope === 'consultant' && groups.length > 0)) && (
          <>
            {/* Controls */}
            <div className="consultant-sowb-breakdown__controls">
          <div className="consultant-sowb-breakdown__selectors">
            {scope === 'consultant' && (
              <div className="consultant-sowb-breakdown__selector-group">
                <label className="consultant-sowb-breakdown__selector-label">
                  Group
                </label>
                <div className="consultant-sowb-breakdown__selector-wrapper">
                  <select
                    className="consultant-sowb-breakdown__select"
                    value={selectedGroupId ?? ''}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSelectedGroupId(next);
                      localStorage.setItem(GROUP_STORAGE_KEY, next);
                    }}
                    disabled={groupsLoading || groups.length === 0}
                  >
                    {groupsLoading ? (
                      <option value="">Loading groups...</option>
                    ) : groups.length === 0 ? (
                      <option value="">No groups created</option>
                    ) : (
                      groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.groupName}
                        </option>
                      ))
                    )}
                  </select>
                  <span className="consultant-sowb-breakdown__select-icon">
                    <span className="material-symbols-outlined">expand_more</span>
                  </span>
                </div>
              </div>
            )}
            {scope === 'enterprise' && (
              <div className="consultant-sowb-breakdown__selector-group">
                <label className="consultant-sowb-breakdown__selector-label">
                  Client Cohort
                </label>
                <div className="consultant-sowb-breakdown__selector-wrapper">
                  <select
                    className="consultant-sowb-breakdown__select"
                    value={selectedCohort}
                    onChange={(e) => setSelectedCohort(e.target.value)}
                  >
                    <option>BS Political Science</option>
                    <option>BS Psychology</option>
                    <option>BA Literature</option>
                    <option>BS Computer Science</option>
                    <option>BS Engineering</option>
                  </select>
                  <span className="consultant-sowb-breakdown__select-icon">
                    <span className="material-symbols-outlined">expand_more</span>
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="consultant-sowb-breakdown__sample-size">
            <span className="material-symbols-outlined">groups</span>
            <span>Sample Size: <strong>N = {scope === 'consultant' ? (overview?.counts?.withAssessments ?? '–') : '1,240'}</strong></span>
          </div>
        </div>

        {/* Table */}
        <div className="consultant-sowb-breakdown__table-container">
          <table className="consultant-sowb-breakdown__table">
            <thead>
              <tr className="consultant-sowb-breakdown__table-header">
                <th className="consultant-sowb-breakdown__table-header-cell">Attribute</th>
                <th className="consultant-sowb-breakdown__table-header-cell">Score (0-10)</th>
                <th className="consultant-sowb-breakdown__table-header-cell">Trend (YoY)</th>
                <th className="consultant-sowb-breakdown__table-header-cell">Status</th>
                <th className="consultant-sowb-breakdown__table-header-cell">Action</th>
              </tr>
            </thead>
            <tbody>
              {sowbAttributes.map((attribute) => (
                <tr
                  key={attribute.id}
                  className="consultant-sowb-breakdown__table-row"
                >
                  <td className="consultant-sowb-breakdown__table-cell">
                    <div className="consultant-sowb-breakdown__attribute-info">
                      <div className={`consultant-sowb-breakdown__attribute-icon consultant-sowb-breakdown__attribute-icon--${attribute.id}`}>
                        <span className="material-symbols-outlined">{attribute.icon}</span>
                      </div>
                      <div className="consultant-sowb-breakdown__attribute-details">
                        <p className="consultant-sowb-breakdown__attribute-name">
                          {attribute.name}
                        </p>
                        <p className="consultant-sowb-breakdown__attribute-description">
                          {attribute.description}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="consultant-sowb-breakdown__table-cell">
                    <span className="consultant-sowb-breakdown__score">
                      {attribute.score.toFixed(1)}
                    </span>
                  </td>
                  <td className="consultant-sowb-breakdown__table-cell">
                    <div className={`consultant-sowb-breakdown__trend consultant-sowb-breakdown__trend--${getTrendColor(attribute.trend)}`}>
                      <span className="material-symbols-outlined">
                        {getTrendIcon(attribute.trend)}
                      </span>
                      <span>–</span>
                    </div>
                  </td>
                  <td className="consultant-sowb-breakdown__table-cell">
                    <span className={`consultant-sowb-breakdown__status consultant-sowb-breakdown__status--${getStatusColor(attribute.status)}`}>
                      <span className="consultant-sowb-breakdown__status-dot"></span>
                      {attribute.status}
                    </span>
                  </td>
                  <td className="consultant-sowb-breakdown__table-cell">
                    <span className="consultant-sowb-breakdown__action-icon">
                      <span className="material-symbols-outlined">chevron_right</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Info */}
        <div className="consultant-sowb-breakdown__pagination">
          <p className="consultant-sowb-breakdown__pagination-info">
            Showing 1 to 8 of 10 attributes
          </p>
          <div className="consultant-sowb-breakdown__pagination-controls">
            <button className="consultant-sowb-breakdown__pagination-btn consultant-sowb-breakdown__pagination-btn--disabled">
              Previous
            </button>
            <button className="consultant-sowb-breakdown__pagination-btn">
              Next
            </button>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConsultantSowbBreakdownPage;




